import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { HttpClient, HttpEvent, HttpHeaders, HttpResponse } from '@angular/common/http';
import { SupportService } from 'src/app/services/support.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-kb-article-form',
  templateUrl: './kb-article-form.component.html',
  styleUrls: ['./kb-article-form.component.scss']
})
export class KbArticleFormComponent implements OnInit {
  form!: FormGroup;
  isEditMode = false;
  articleId = '';
  submitting = false;
  categories: any[] = [];

  editorConfig!: AngularEditorConfig;

  constructor(
    private fb: FormBuilder,
    private supportService: SupportService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    if (localStorage.getItem('role') !== 'admin') {
      this.router.navigate(['/support']);
      return;
    }

    const token = localStorage.getItem('token') || '';
    this.editorConfig = {
      editable: true,
      spellcheck: true,
      height: '300px',
      minHeight: '200px',
      placeholder: 'Enter article content here...',
      translate: 'no',
      defaultParagraphSeparator: 'p',
      defaultFontName: '',
      toolbarHiddenButtons: [
        ['subscript', 'superscript'],
        ['insertVideo']
      ],
      uploadWithCredentials: false,
      upload: (file: File) => {
        const fd = new FormData();
        fd.append('image', file);
        const hdrs = new HttpHeaders({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });
        return this.http.post<HttpEvent<{ imageUrl: string }>>(
          `${environment.baseUrl}support/articles/upload-image`,
          fd,
          { headers: hdrs, observe: 'events', reportProgress: true }
        ) as any;
      }
    };

    this.form = this.fb.group({
      title: ['', Validators.required],
      category: ['', Validators.required],
      tags: [''],
      status: [1],
      content: ['', Validators.required],
      audience: [[]]
    });

    this.loadCategories();

    this.articleId = this.route.snapshot.paramMap.get('id') || '';
    if (this.articleId) {
      this.isEditMode = true;
      this.loadArticle();
    }
  }

  loadCategories(): void {
    this.supportService.getKbCategories().subscribe({
      next: (res: any) => {
        this.categories = res.response?.data || [];
      },
      error: () => {}
    });
  }

  loadArticle(): void {
    this.spinner.show();
    this.supportService.getArticle(this.articleId).subscribe({
      next: (res: any) => {
        const article = res.response?.data?.article || res.response?.data || {};
        this.form.patchValue({
          title: article.title || '',
          category: article.category || '',
          tags: Array.isArray(article.tags) ? article.tags.join(', ') : (article.tags || ''),
          status: article.status !== undefined ? article.status : 1,
          content: article.content || '',
          audience: Array.isArray(article.audience) ? article.audience : []
        });
        this.spinner.hide();
      },
      error: (err: any) => {
        this.spinner.hide();
        this.toastr.error(err.error?.response?.message || 'Failed to load article');
      }
    });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const val = this.form.value;
    const tagsArray = (val.tags || '')
      .split(',')
      .map((t: string) => t.trim())
      .filter(Boolean);

    const payload = {
      title: val.title,
      category: val.category,
      tags: tagsArray,
      status: +val.status,
      content: val.content,
      audience: val.audience || []
    };

    this.submitting = true;
    this.spinner.show();

    if (this.isEditMode) {
      this.supportService.updateArticle(this.articleId, payload).subscribe({
        next: (res: any) => {
          this.submitting = false;
          this.spinner.hide();
          this.toastr.success('Article updated successfully');
          this.router.navigate(['/support/articles', this.articleId]);
        },
        error: (err: any) => {
          this.submitting = false;
          this.spinner.hide();
          this.toastr.error(err.error?.response?.message || 'Failed to update article');
        }
      });
    } else {
      this.supportService.createArticle(payload).subscribe({
        next: (res: any) => {
          this.submitting = false;
          this.spinner.hide();
          this.toastr.success('Article created successfully');
          const newId = res.response?.data?.article?._id || res.response?.data?._id || res.response?._id || '';
          this.router.navigate(['/support/articles', newId]);
        },
        error: (err: any) => {
          this.submitting = false;
          this.spinner.hide();
          this.toastr.error(err.error?.response?.message || 'Failed to create article');
        }
      });
    }
  }

  readonly AUDIENCE_ROLES = [
    { value: 'admin', label: 'Admin' },
    { value: 'manager', label: 'Store Manager' },
    { value: 'chain_manager', label: 'Chain Manager' },
    { value: 'clerk', label: 'Clerk' },
    { value: 'receipt_auditor', label: 'Receipt Auditor' },
  ];

  isAudienceSelected(role: string): boolean {
    const current: string[] = this.form.get('audience')?.value || [];
    return current.includes(role);
  }

  toggleAudience(role: string): void {
    const current: string[] = [...(this.form.get('audience')?.value || [])];
    const idx = current.indexOf(role);
    if (idx > -1) {
      current.splice(idx, 1);
    } else {
      current.push(role);
    }
    this.form.get('audience')?.setValue(current);
  }

  goBack(): void {
    this.router.navigate(['/support']);
  }

  onEditorPaste(event: ClipboardEvent): void {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.type.startsWith('image/')) continue;

      const file = item.getAsFile();
      if (!file) continue;

      event.preventDefault();

      const token = localStorage.getItem('token') || '';
      const formData = new FormData();
      formData.append('image', file);

      const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
      this.http.post<{ imageUrl: string }>(
        `${environment.baseUrl}support/articles/upload-image`,
        formData,
        { headers }
      ).subscribe({
        next: (res) => {
          const imgTag = `<img src="${res.imageUrl}" alt="pasted image" style="max-width:100%;">`;
          document.execCommand('insertHTML', false, imgTag);
        },
        error: () => {
          this.toastr.error('Failed to upload pasted image');
        }
      });
      break;
    }
  }
}
